/**
 * Shared intake-date helpers for the admin enrolment wizard and any other
 * flow that needs to pick the next cohort start.
 *
 * Business rule (as set by admissions):
 *   Intakes run twice a month —
 *     • the FIRST Monday of each month
 *     • the THIRD Monday of each month  (i.e. the Monday of the third week
 *       counting from Monday 1)
 *   No exceptions across programs.
 *
 * End dates are computed from the program's `intended_duration_days`, with
 * fallbacks by credential type (diploma = 240 days, certificate = 90 days).
 */

/** Returns the nth Monday (1-based) of the given year+month, or null. */
export function nthMondayOfMonth(year, monthIndex0, n) {
    const firstOfMonth = new Date(year, monthIndex0, 1);
    const dow = firstOfMonth.getDay(); // 0=Sun ... 1=Mon
    const daysUntilMon = (1 - dow + 7) % 7;
    const firstMon = 1 + daysUntilMon;
    const target = firstMon + (n - 1) * 7;
    const last = new Date(year, monthIndex0 + 1, 0).getDate();
    if (target > last) return null;
    return new Date(year, monthIndex0, target);
}

/** ISO YYYY-MM-DD in local time (matches <input type="date"> convention). */
export function toIso(d) {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}

/** Format a Date as "Mon, 6 Apr 2026" — used in select labels. */
export function fmtIntakeLabel(d) {
    if (!d) return "";
    return d.toLocaleDateString(undefined, {
        weekday: "short", day: "numeric", month: "short", year: "numeric",
    });
}

/**
 * Build a list of upcoming intake Mondays (1st + 3rd Monday of the month)
 * for the next `months` calendar months starting today. Excludes past dates.
 *
 * @returns array of `{ iso, label, date }`
 */
export function upcomingIntakes(months = 6, from = new Date()) {
    const startY = from.getFullYear();
    const startM = from.getMonth();
    const cutoff = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const out = [];
    for (let i = 0; i < months + 1; i++) {
        const y = startY + Math.floor((startM + i) / 12);
        const m = (startM + i) % 12;
        for (const n of [1, 3]) {
            const d = nthMondayOfMonth(y, m, n);
            if (!d) continue;
            if (d < cutoff) continue;
            out.push({
                iso: toIso(d),
                label: fmtIntakeLabel(d),
                date: d,
                which: n === 1 ? "1st Monday" : "3rd Monday",
            });
        }
    }
    return out;
}

/**
 * Compute the end date for a given start date + program object.
 *
 *   duration_days = program.intended_duration_days
 *                    ?? (credential_type === "diploma" ? 240 : 90)
 */
export function computeEndDate(startIso, program) {
    if (!startIso || !program) return "";
    const days = Number(program.intended_duration_days)
        || (program.credential_type === "diploma" ? 240 : 90);
    const [y, m, d] = startIso.split("-").map(Number);
    if (!y || !m || !d) return "";
    const start = new Date(y, m - 1, d);
    const end = new Date(start);
    end.setDate(end.getDate() + days);
    return toIso(end);
}
